import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { Anchor, Badge, Box, Drawer, Group, SegmentedControl, Stack, Tabs, Text, TextInput, Title } from '@mantine/core';
import { useHotkeys } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import { CHEATSHEETS, GLOSSARY, searchGlossary, type GlossaryTerm } from '@flow/core';
import { useQueryState } from '../../app/routes';

type Event = keyof typeof CHEATSHEETS;
const EVENT_TABS: { value: Event; label: string }[] = [{ value: 'pf', label: 'PF' }, { value: 'ld', label: 'LD' }, { value: 'policy', label: 'Policy' }, { value: 'parli', label: 'Parli' }];

type Tab = 'glossary' | 'cheatsheet';
interface KnowledgeCtx {
  open: (tab?: Tab, query?: string) => void;
  /** Which event's cheatsheet to show; a round sets its own when it opens. */
  setEvent: (e: Event) => void;
}
const Ctx = createContext<KnowledgeCtx>({ open: () => {}, setEvent: () => {} });
export const useKnowledge = () => useContext(Ctx);

const TermCard = ({ t, onRelated }: { t: GlossaryTerm; onRelated: (q: string) => void }) => (
  <Box py="sm" style={{ borderBottom: '1px solid var(--grid-line)' }}>
    <Group gap={6} mb={2}>
      <Text fw={600}>{t.term}</Text>
      {t.aliases?.map((a) => (
        <Badge key={a} size="xs" variant="light" color="gray">{a}</Badge>
      ))}
    </Group>
    <Text size="sm">{t.short}</Text>
    {t.example && <Text size="sm" c="dimmed" fs="italic" mt={4}>Example: {t.example}</Text>}
    {t.why && <Text size="sm" mt={4}><Text span fw={600} size="sm">Why it matters: </Text>{t.why}</Text>}
    {t.related?.length ? (
      <Text size="xs" c="dimmed" mt={4}>
        Related:{' '}
        {t.related.map((r, i) => (
          <span key={r}>
            {i > 0 && ' · '}
            <Anchor size="xs" onClick={() => onRelated(r)}>{r}</Anchor>
          </span>
        ))}
      </Text>
    ) : null}
  </Box>
);

/** Glossary + cheatsheet, available anywhere (⌘G), including mid-round. Open state lives in the URL: `?ref=glossary&q=turn`. */
export const KnowledgeProvider = ({ children }: { children: ReactNode }) => {
  const url = useQueryState();
  const ref = url.get('ref');
  const opened = ref === 'glossary' || ref === 'cheatsheet';
  const tab: Tab = ref === 'cheatsheet' ? 'cheatsheet' : 'glossary';
  const q = url.get('q') ?? '';
  const [event, setEvent] = useState<Event>('pf');
  const results = useMemo(() => searchGlossary(GLOSSARY, q), [q]);
  const { set } = url;
  const ctx = useMemo<KnowledgeCtx>(() => ({
    open: (t = 'glossary', query) => {
      const already = ['glossary', 'cheatsheet'].includes(new URLSearchParams(window.location.search).get('ref') ?? '');
      set({ ref: t, ...(query !== undefined ? { q: query } : {}) }, { replace: already });
    },
    setEvent,
  }), [set]);
  const setTab = (t: Tab) => set({ ref: t }, { replace: true });
  const setQ = (v: string) => set({ q: v }, { replace: true });

  useHotkeys([
    ['mod+g', () => ctx.open('glossary')],
    ['mod+/', () => ctx.open('cheatsheet')],
  ], []);

  return (
    <Ctx.Provider value={ctx}>
      {children}
      <Drawer opened={opened} onClose={() => url.close('ref', 'q')} position="right" size="md" title={<Title order={2} size="h5">Judge's reference</Title>} zIndex={400}>
        <Tabs value={tab} onChange={(v) => setTab((v as Tab) ?? 'glossary')}>
          <Tabs.List mb="sm">
            <Tabs.Tab value="glossary">Glossary</Tabs.Tab>
            <Tabs.Tab value="cheatsheet">Cheatsheet</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="glossary">
            <TextInput data-autofocus placeholder="Search: turn, extend, weigh…" leftSection={<IconSearch size={14} />} value={q} onChange={(e) => setQ(e.currentTarget.value)} mb="xs" aria-label="Search glossary" />
            {results.length === 0 && <Text c="dimmed" size="sm">No match. Try a shorter word.</Text>}
            {results.map((t) => (
              <TermCard key={t.term} t={t} onRelated={setQ} />
            ))}
          </Tabs.Panel>
          <Tabs.Panel value="cheatsheet">
            <Stack gap="lg">
              <SegmentedControl size="xs" value={event} onChange={(v) => setEvent(v as Event)} data={EVENT_TABS} data-testid="cheatsheet-event" />
              {CHEATSHEETS[event].map((s) => (
                <Box key={s.title}>
                  <Text fw={700} mb={4}>{s.title}</Text>
                  {s.items.map((i) => (
                    <Box key={i.h} mb={8}>
                      <Text size="sm" fw={600}>{i.h}</Text>
                      <Text size="sm">{i.body}</Text>
                    </Box>
                  ))}
                </Box>
              ))}
              <Text size="xs" c="dimmed">Items marked (verify) depend on tournament rules. Check the invitation or ask tab.</Text>
            </Stack>
          </Tabs.Panel>
        </Tabs>
      </Drawer>
    </Ctx.Provider>
  );
};

/** Inline glossary term: dotted underline, opens the glossary on that term. */
export const Term = ({ children, q }: { children: ReactNode; q?: string }) => {
  const k = useKnowledge();
  const query = q ?? (typeof children === 'string' ? children : '');
  const hit = GLOSSARY.find((t) => [t.term, ...(t.aliases ?? [])].some((a) => a.toLowerCase() === query.toLowerCase()));
  return (
    <Text
      span
      inherit
      title={hit?.short}
      onClick={() => k.open('glossary', query)}
      style={{ textDecoration: 'underline dotted', textUnderlineOffset: 3, cursor: 'help' }}
    >
      {children}
    </Text>
  );
};
